import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import { CheckCircle, AlertTriangle, FileText, Loader2 } from "lucide-react";

const DOCUMENT_TYPES = [
  { value: "polizza", label: "Polizza" },
  { value: "carta_identita", label: "Carta d'Identit\u00e0" },
  { value: "patente", label: "Patente" },
  { value: "libretto_circolazione", label: "Libretto di Circolazione" },
  { value: "certificato_proprieta", label: "Certificato di Propriet\u00e0" },
  { value: "visura_camerale", label: "Visura Camerale" },
  { value: "codice_fiscale", label: "Codice Fiscale / Tessera Sanitaria" },
  { value: "attestato_rischio", label: "Attestato di Rischio" },
  { value: "contrassegno", label: "Contrassegno" },
  { value: "quietanza", label: "Quietanza" },
  { value: "preventivo", label: "Preventivo" },
  { value: "sinistro", label: "Sinistro" },
  { value: "fattura", label: "Fattura" },
  { value: "contratto", label: "Contratto" },
  { value: "documento", label: "Documento Generico" },
  { value: "altro", label: "Altro" },
];

export default function DocumentConfirmDialog({
  open,
  onOpenChange,
  analysisData,
  onConfirm,
  confirming,
}) {
  const [selectedType, setSelectedType] = useState(
    analysisData?.document_type || "documento"
  );

  const confidence = analysisData?.confidence || 0;
  const identifier = analysisData?.identifier || "";
  const suggestedName = analysisData?.suggested_name || "";
  const extractedData = analysisData?.extracted_data || {};

  const handleConfirm = () => {
    onConfirm(selectedType);
  };

  const confidenceColor =
    confidence >= 0.8
      ? "text-green-600"
      : confidence >= 0.5
      ? "text-amber-600"
      : "text-red-600";

  const typeLabel =
    DOCUMENT_TYPES.find((t) => t.value === selectedType)?.label || selectedType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="document-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Conferma Tipo Documento
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* AI Classification Result */}
          <div className="p-4 bg-muted/50 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">L'AI ha riconosciuto:</span>
              {confidence > 0 && (
                <Badge variant="outline" className={confidenceColor}>
                  {Math.round(confidence * 100)}% sicurezza
                </Badge>
              )}
            </div>

            {/* Document Type Selector */}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">
                Tipo documento
              </label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger data-testid="document-type-select">
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Identifier */}
            {identifier && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Identificativo rilevato
                </label>
                <p className="text-sm font-medium">{identifier}</p>
              </div>
            )}

            {/* Suggested filename */}
            {suggestedName && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Nome file suggerito
                </label>
                <p className="text-sm truncate" title={suggestedName}>
                  {suggestedName}
                </p>
              </div>
            )}
          </div>

          {/* Extracted Data Summary */}
          {Object.keys(extractedData).length > 0 && (
            <div className="p-3 border rounded-lg space-y-2">
              <span className="text-sm font-medium">Dati estratti:</span>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(extractedData)
                  .filter(([, v]) => v)
                  .slice(0, 8)
                  .map(([key, value]) => (
                    <div key={key} className="text-xs">
                      <span className="text-muted-foreground">
                        {key.replace(/_/g, " ")}:
                      </span>{" "}
                      <span className="font-medium">{String(value)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Low confidence warning */}
          {confidence > 0 && confidence < 0.6 && (
            <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950 rounded text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Confidenza bassa. Verifica che il tipo documento sia corretto
                prima di confermare.
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={confirming}
            data-testid="document-confirm-cancel"
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={confirming}
            data-testid="document-confirm-save"
          >
            {confirming ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Conferma e Salva
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
